import { DATE_FORMAT, formatShortISO, parse } from '@eneco/common/date';
import { ApiError } from '@eneco/dc/client/ApiError';
import { putProductsOrderV2 } from '@eneco/dc/services/ProductsPublicService';
import { MachineConfig } from '@eneco/flows/types';
import {
  DC_Domain_Models_General_Address,
  Products_Offers_V3_OfferResponseModel,
  Products_Order_ProductOffering,
  Products_Orders_SwitchTypeResponse,
  RequestModels_Products_Order_ProductOrder,
  ResponseModels_Products_Orders_CreateOrderResponseLocal,
} from '@monorepo-types/dc';
import { assign, setup, fromPromise } from 'xstate';

import { FlowContextSme } from '../context';
import { getProductTypesFromEnergyType, selectOfferProductCombination } from '../helpers';

const salutationToGender: Record<string, 'male' | 'female' | 'unknown'> = {
  dhr: 'male',
  mevr: 'female',
};

interface OrderMachineContext {
  id?: string | null;
  orderTypes?: Array<Products_Orders_SwitchTypeResponse> | null;
  errorMessage?: string;
}

interface OrderMachineEvent {
  type: 'SEND_ORDER' | 'NOT_AUTHENTICATED';
  config: MachineConfig;
  values: FlowContextSme;
  offer: Products_Offers_V3_OfferResponseModel;
}

const getElectricityUsage = (flowData: FlowContextSme): Record<string, string> => {
  return {
    ...(flowData.usageElectricityHigh && {
      tariff: flowData.usageElectricityHigh.toString(),
    }),
    ...(flowData.usageElectricityLow && {
      lowTariff: flowData.usageElectricityLow.toString(),
    }),
    ...(flowData.solarPanelsOutput && {
      redeliveryTariff: flowData.solarPanelsOutput.toString(),
    }),
  };
};

const getGasUsage = (flowData: FlowContextSme): Record<string, string> => {
  return {
    ...(flowData.usageGas && {
      tariff: flowData.usageGas.toString(),
    }),
  };
};

const getWarmthUsage = (flowData: FlowContextSme): Record<string, string> => {
  return {
    ...(flowData.usageWarmth && {
      tariff: flowData.usageWarmth.toString(),
    }),
  };
};

const getWarmWaterUsage = (flowData: FlowContextSme): Record<string, string> => {
  return {
    ...(flowData.usageWater && {
      tariff: flowData.usageWater.toString(),
    }),
  };
};

const getProductCombination = (offersData: Products_Offers_V3_OfferResponseModel, flowData: FlowContextSme) => {
  const selectedProductTypes = [...getProductTypesFromEnergyType(flowData.energyType), 'cashback' as const];

  return selectOfferProductCombination(
    offersData?.productCombinations ?? [],
    selectedProductTypes,
    flowData.offerType,
    !!flowData.hasUpsellGas,
    !!flowData.hasUpsellElectricity,
  );
};

const getDeliveryAddress = (flowData: FlowContextSme): DC_Domain_Models_General_Address => {
  return {
    type: 'Delivery',
    postalCode: flowData.postalCode,
    houseNumber: flowData.houseNumber,
    houseNumberSuffix: flowData.houseNumberSuffix,
    street: flowData.street,
    city: flowData.city,
    hasResidentialPurpose: flowData.hasResidencePurpose,
  };
};

const constructProductsList = (
  flowData: FlowContextSme,
  offersData: Products_Offers_V3_OfferResponseModel,
): Array<Products_Order_ProductOffering> => {
  const productCombination = getProductCombination(offersData, flowData);

  const startDate = flowData.deliveryDate && formatShortISO(parse(flowData.deliveryDate, DATE_FORMAT));
  const actionReason = flowData.isMoving ? 'MoveIn' : 'Switch';

  const products: Array<Products_Order_ProductOffering> = [];

  productCombination?.products?.forEach(element => {
    // we skip "cashback" product since it is not an actual
    // product within the CRM system....
    if (element?.productType === 'cashback') {
      return;
    }

    const offerProduct = offersData?.products?.find(op => op.marketingProductCode === element.marketingProductCode);

    const product: Products_Order_ProductOffering = {
      productType: offerProduct?.productType ?? 'unknown',
      productName: offerProduct?.description ?? undefined,
      usage: {},
      startDate,
      marketingProductCode: element.marketingProductCode,
      code: element.marketingProductCode,
      action: 'Add',
      actionReason,
    };

    switch (offerProduct?.productType) {
      case 'electricity':
        product.usage = getElectricityUsage(flowData);
        break;

      case 'gas':
        product.usage = getGasUsage(flowData);
        break;

      case 'warmth':
        product.usage = getWarmthUsage(flowData);
        break;

      case 'warmthEkv':
        product.usage = getWarmthUsage(flowData);
        break;

      case 'tapwater':
        product.usage = getWarmWaterUsage(flowData);
        break;
    }

    products.push(product);
  });

  return products;
};

const sendOrderService = (
  config: MachineConfig,
  flowData: FlowContextSme,
  offersData: Products_Offers_V3_OfferResponseModel,
): Promise<{ data?: ResponseModels_Products_Orders_CreateOrderResponseLocal }> =>
  new Promise((resolve, reject) => {
    const SME_CUSTOMER_TURNOVER_REQUIREMENT = 'lessThenTwoMillion';
    const SME_CLIENT_EMPLOYEE_COUNT_REQUIREMENT = 'lessThenTen';

    const productCombination = getProductCombination(offersData, flowData);

    if (!productCombination) {
      reject(new Error('No matching product combination found for the selected offer'));
      return;
    }

    const requestBody: RequestModels_Products_Order_ProductOrder = {
      data: {
        productOffers: constructProductsList(flowData, offersData),
        customer: {
          isNew: true,
          person: {
            firstName: flowData.firstName ?? '',
            initials: flowData.initials ?? '',
            surnamePreposition: flowData.surnamePreposition,
            surname: flowData.surname ?? '',
            gender: flowData.salutation ? (salutationToGender[flowData.salutation] ?? 'unknown') : 'unknown',
            dateOfBirth: flowData.birthdate && formatShortISO(parse(flowData.birthdate, DATE_FORMAT)),
          },
          contact: {
            address: {
              type: 'Residential',
              postalCode: flowData.postalCode,
              houseNumber: flowData.houseNumber,
              houseNumberSuffix: flowData.houseNumberSuffix,
              street: flowData.street,
              city: flowData.city,
              hasResidentialPurpose: flowData.hasResidencePurpose,
            },
            phoneNumbers: [flowData.phoneNumber?.replace(/[^\d]/g, '') ?? ''],
            emailAddress: flowData.emailAddress,
            contactPreferences: {
              contactByTelephone: Boolean(flowData.C2),
              productOffersFirstParty: false,
              productOffersFirstPartySubscribed: Boolean(flowData.C1),
              productOffersThirdParty: Boolean(flowData.C3),
            },
          },
          organisation: {
            name: flowData.companyName,
            chamberOfCommerceNumber: flowData.chamberOfCommerceNumber,
            // will always be false. An customer with private use wil be redirected to the consumer flow
            hasPrivateUse: false,
          },
          bank: {
            number: flowData.iban?.replace(/\s/g, ''),
            autoDebetConsent: true,
            paymentMethod: 'directDebit',
          },
        },
        deliveryAddress: getDeliveryAddress(flowData),
        processingType: 'distantSelling',
        configuration: {
          marketingCampaignCode: offersData?.configuration?.marketingCampaignCode,
          actionCode: offersData?.configuration?.actionCode,
          promotionCode: productCombination.discountCode,
          discountType: productCombination.discountType ?? 'unknown',
          digimonksExternalId: offersData?.configuration?.digimonksExternalId,
        },
        isResidence: flowData.hasResidencePurpose,
        referenceId: flowData.basketId,
        // The naming of `b2BCustomerCategoryCriteria` property is missleading, since SME clients are the part of b2b domain.
        // The correct conditions for the SME customers are:
        //    - isTurnoverGreaterThan2Million: false
        //    - isEmployeeCountGreaterThan10: false
        b2BCustomerCategoryCriteria: {
          isTurnoverGreaterThan2Million: flowData.revenue !== SME_CUSTOMER_TURNOVER_REQUIREMENT,
          isEmployeeCountGreaterThan10: flowData.employeeCount !== SME_CLIENT_EMPLOYEE_COUNT_REQUIREMENT,
        },
      },
    };

    return putProductsOrderV2({
      businessUnit: config.businessUnit,
      label: config.label,
      requestBody,
    })
      .then(response => {
        resolve({ data: response });
      })
      .catch((error: ApiError) => {
        reject(new Error(error?.message));
      });
  });

const actors = {
  send: fromPromise(async ({ input }: { input: { context: OrderMachineContext; event: OrderMachineEvent } }) => {
    const { event } = input;
    return await sendOrderService(event.config, event.values, event.offer);
  }),
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const orderMachine: any = setup({
  types: {} as {
    context: OrderMachineContext;
    events: OrderMachineEvent;
  },
  actors,
}).createMachine({
  id: 'order',
  initial: 'IDLE',
  context: {},
  on: {
    SEND_ORDER: { target: '.FETCHING' },
  },
  states: {
    IDLE: {},
    FETCHING: {
      invoke: {
        src: 'send',
        input: ({ context, event }) => ({ context, event }),
        onDone: {
          target: 'SUCCESS',
          actions: [
            assign(({ context, event }) => ({
              ...context,
              ...event.output?.data?.data,
            })),
          ],
        },
        onError: [
          {
            target: 'ERROR_NOT_AUTHENTICATED',
            guard: ({ event }) => (event as { error?: Error })?.error?.message === 'Unauthorized',
            actions: [
              assign(({ context, event }) => ({
                ...context,
                errorMessage: (event as { error?: Error })?.error?.message,
              })),
            ],
          },
          {
            target: 'ERROR',
            actions: [
              assign(({ context, event }) => ({
                ...context,
                errorMessage: (event as { error?: Error })?.error?.message,
              })),
            ],
          },
        ],
      },
    },
    SUCCESS: {
      entry: [
        assign(({ context }) => ({
          ...context,
          errorMessage: undefined,
        })),
      ],
      type: 'final' as const,
    },
    ERROR: {},
    ERROR_NOT_AUTHENTICATED: {},
  },
});
